
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AIAnalysisResult, NotificationProductData } from "@/types/notifications";
import { useNotificationUpdate } from "./useNotificationUpdate";

export interface Notification {
  id: string;
  type: string;
  vo_number: string | null;
  exact_order_number: string | null;
  message: string;
  priority: string;
  status: string;
  user_id: string;
  user_name: string;
  created_at: string;
  updated_at: string;
  archived: boolean;
  is_deleted: boolean;
  deleted_at: string | null;
  products_monitored?: boolean;
  products?: NotificationProductData[];
}

export interface NotificationEditHistory {
  id: string;
  notification_id: string;
  editor_id: string;
  editor_name: string;
  edit_date: string;
  previous_message: string | null;
  new_message: string | null;
  field_name: string | null;
  previous_value: string | null;
  new_value: string | null;
  change_type: string | null;
}

export const useNotificationDetail = (id: string | undefined) => {
  const [editMode, setEditMode] = useState(false);
  const [editedNotification, setEditedNotification] = useState<Notification | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showEditHistory, setShowEditHistory] = useState(false);

  // Fetch notification details
  const {
    data: notification,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["notification", id],
    queryFn: async () => {
      if (!id) throw new Error("Notification ID is required");

      console.log("Fetching notification details for ID:", id);

      try {
        // Get notification details
        const { data: notificationData, error: notificationError } = await supabase
          .from("notifications")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (notificationError) {
          console.error("Error fetching notification:", notificationError);
          throw new Error(`Failed to fetch notification: ${notificationError.message}`);
        }

        if (!notificationData) {
          throw new Error("Notification not found");
        }

        console.log("Fetched notification data:", notificationData);

        // Get related products
        const { data: productsData, error: productsError } = await supabase
          .from("notification_product_monitoring")
          .select("*")
          .eq("notification_id", id);

        if (productsError) {
          console.error("Error fetching products:", productsError);
        } else {
          console.log("Fetched products data:", productsData || []);
        }

        // Fetch image URLs and VO numbers from orders_live_ls
        let productsWithImages = productsData || [];
        const isBulk = notificationData.vo_number?.includes(",") 
          || /VO\d+.*VO\d+/i.test(notificationData.message || "");
        
        if (productsWithImages.length > 0 && notificationData.vo_number) {
          const skus = productsWithImages.map((p: any) => p.product_sku).filter(Boolean);
          if (skus.length > 0) {
            const voNumbers = notificationData.vo_number.split(",").map((v: string) => {
              const trimmed = v.trim().replace(/\s+/g, '').toUpperCase();
              return trimmed.startsWith('VO') ? trimmed : `VO${trimmed}`;
            }).filter((v: string) => v.length > 2);

            let { data: orderRows } = await supabase
              .from("orders_live_ls")
              .select("sku, image_url, ordernumber")
              .in("ordernumber", voNumbers)
              .in("sku", skus);

            if (orderRows) {
              const imageMap = new Map<string, string | null>();
              // For bulk: map SKU to VO number
              const skuToVoMap = new Map<string, string>();
              
              for (const r of orderRows as any[]) {
                if (!imageMap.get(r.sku) && r.image_url) {
                  imageMap.set(r.sku, r.image_url);
                } else if (!imageMap.has(r.sku)) {
                  imageMap.set(r.sku, r.image_url);
                }
                // Map SKU to VO number for bulk
                if (isBulk && r.ordernumber) {
                  skuToVoMap.set(r.sku, r.ordernumber);
                }
              }

              // Fallback: fetch images from other orders for SKUs still missing
              const missingImageSkus = skus.filter(sku => !imageMap.get(sku));
              if (missingImageSkus.length > 0) {
                const fallbackResults = await Promise.allSettled(
                  missingImageSkus.map(sku =>
                    supabase
                      .from("orders_live_ls")
                      .select("sku, image_url")
                      .eq("sku", sku)
                      .not("image_url", "is", null)
                      .limit(1)
                  )
                );
                for (const result of fallbackResults) {
                  if (result.status === "fulfilled" && result.value.data) {
                    for (const row of result.value.data) {
                      if (row.image_url && row.sku) {
                        imageMap.set(row.sku, row.image_url);
                      }
                    }
                  }
                }
              }

              productsWithImages = productsWithImages.map((p: any) => ({
                ...p,
                image_url: imageMap.get(p.product_sku) || null,
                vo_number: isBulk ? (skuToVoMap.get(p.product_sku) || null) : null
              }));
            }
          }
        }

        // Fetch stock from Goes (magazijn 2)
        const productSkus = productsWithImages.map((p: any) => p.product_sku).filter(Boolean);
        if (productSkus.length > 0) {
          const { data: stockData } = await supabase
            .from('voorraad_live')
            .select('product_id, voorraad')
            .in('product_id', productSkus)
            .eq('magazijn', '2');

          const stockMap = new Map<string, number>();
          stockData?.forEach((item: any) => stockMap.set(item.product_id, item.voorraad || 0));

          productsWithImages = productsWithImages.map((p: any) => ({
            ...p,
            stock_goes: stockMap.get(p.product_sku) ?? null
          }));
        }

        // Filter roerhoutjes for bulk notifications
        const ROERHOUTJES_SKU = "999-A0000193-0001";
        if (isBulk) {
          productsWithImages = productsWithImages.filter((p: any) =>
            p.product_sku !== ROERHOUTJES_SKU || p.product_quantity > 1
          );
        }

        return {
          ...notificationData,
          products: productsWithImages
        };
      } catch (error) {
        console.error("Error in notification query function:", error);
        throw error;
      }
    },
    enabled: !!id,
    retry: (failureCount, error) => {
      // Don't retry if it's a "not found" error
      if (error instanceof Error && error.message.includes("not found")) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    }
  });

  // Fetch edit history
  const {
    data: editHistory,
    isLoading: isLoadingHistory
  } = useQuery({
    queryKey: ["notification-history", id],
    queryFn: async () => {
      if (!id) throw new Error("Notification ID is required");

      console.log("Fetching notification history for ID:", id);

      const { data, error: historyError } = await supabase
        .from("notification_edit_history")
        .select("*")
        .eq("notification_id", id)
        .order("edit_date", { ascending: false });

      if (historyError) {
        console.error("Error fetching notification history:", historyError);
        throw historyError;
      }

      console.log("Fetched notification history:", data || []);
      return data;
    },
    enabled: !!id && showEditHistory
  });

  // Set edited notification when notification data changes
  useEffect(() => {
    if (notification) {
      console.log("Setting edited notification from fetched data:", notification);
      setEditedNotification(notification);
    }
  }, [notification]);

  const {
    updateNotification,
    handleInputChange,
    handleSubmit,
    resetNotification,
    handleAIAnalysisComplete,
    handleProductsChange
  } = useNotificationUpdate(
    id,
    notification,
    editedNotification,
    setEditedNotification,
    setEditMode
  );

  return {
    notification,
    editedNotification,
    editMode,
    setEditMode,
    isAnalyzing,
    setIsAnalyzing,
    showEditHistory,
    setShowEditHistory,
    isLoading,
    error,
    editHistory,
    isLoadingHistory,
    handleInputChange,
    handleAIAnalysisComplete,
    handleProductsChange,
    handleSubmit,
    resetNotification,
    updateNotification,
    refetch
  };
};
